import { cn, Flag, Plus, X } from "@app/ui";
import type { Frequency, Priority, RuleInput, Task } from "@app/core";
import { childrenOf, subtaskProgress } from "@app/core";
import { useEffect, useMemo, useState } from "react";
import type { I18nKey } from "../../../../lib/language.js";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";
import { useTasksStore } from "../../../../stores/index.js";
import { PRIORITY_FLAG_HEX } from "../../../task-colors.js";
import { toLocalDate } from "../../../task-form/time-helpers.js";
import { DatePicker } from "../../../task-form/DatePicker.js";
import { SheetModal } from "../SheetModal.js";

interface Props {
  recurEnabled: boolean;
  setRecurEnabled: (v: boolean) => void;
  recurRule: RuleInput | null;
  setRecurRule: (r: RuleInput) => void;
  seriesUntilStr: string;
  setSeriesUntilStr: (s: string) => void;
  reminderOffsets: number[];
  setReminderOffsets: (v: number[]) => void;
  showReminders: boolean;
  editing: Task | null;
  pendingSubtasks: string[];
  setPendingSubtasks: (v: string[]) => void;
}

const FREQS: Array<{ id: Frequency; labelKey: I18nKey }> = [
  { id: "daily", labelKey: "repeat.day" },
  { id: "weekly", labelKey: "repeat.week" },
  { id: "monthly", labelKey: "repeat.month" },
  { id: "yearly", labelKey: "repeat.year" },
];

type Weekday = NonNullable<RuleInput["byDay"]>[number];
const WEEKDAYS: Array<{ key: Weekday; date: Date }> = [
  { key: "MO", date: new Date(2024, 0, 1) },
  { key: "TU", date: new Date(2024, 0, 2) },
  { key: "WE", date: new Date(2024, 0, 3) },
  { key: "TH", date: new Date(2024, 0, 4) },
  { key: "FR", date: new Date(2024, 0, 5) },
  { key: "SA", date: new Date(2024, 0, 6) },
  { key: "SU", date: new Date(2024, 0, 7) },
];

const REMINDERS: Array<{ labelKey: I18nKey; min: number; count?: number }> = [
  { labelKey: "reminder.atTime", min: 0 },
  { labelKey: "reminder.minBefore", min: 5, count: 5 },
  { labelKey: "reminder.minBefore", min: 15, count: 15 },
  { labelKey: "reminder.minBefore", min: 30, count: 30 },
  { labelKey: "reminder.hourBefore", min: 60, count: 1 },
  { labelKey: "reminder.dayBefore", min: 1440 },
];

const SUB_PRIORITIES: Priority[] = ["none", "low", "medium", "high"];
const PRI_NAME_KEY: Record<Priority, I18nKey> = {
  none: "taskForm.noPriority",
  low: "sidebar.priority.low",
  medium: "sidebar.priority.medium",
  high: "sidebar.priority.high",
};

const chip = (on: boolean) =>
  cn("rounded-md border px-3 py-1.5 text-sm font-medium", on ? "bg-primary text-primary-foreground" : "bg-background");

function SubtaskPriority({ value, onPick }: { value: Priority; onPick: (priority: Priority) => void }) {
  const { t } = useI18n();
  return (
    <div className="flex shrink-0 gap-0.5" role="group" aria-label={t("sidebar.priority")}>
      {SUB_PRIORITIES.map((p) => {
        const selected = value === p;
        const color = PRIORITY_FLAG_HEX[p] || "currentColor";
        return (
          <button
            key={p}
            type="button"
            title={t(PRI_NAME_KEY[p])}
            aria-label={t(PRI_NAME_KEY[p])}
            aria-pressed={selected}
            onClick={() => onPick(p)}
            className={cn("grid h-7 w-7 place-items-center rounded-full border", selected ? "bg-background ring-2 ring-ring" : "bg-background/80 text-muted-foreground")}
          >
            {p === "none" ? (
              <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
            ) : (
              <Flag className="h-3.5 w-3.5" fill={color} stroke={color} />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Compact repeat, reminder, and subtask controls for the mobile sheet. */
export function RepeatReminderFields(p: Props) {
  const { t: tr, locale } = useI18n();
  const reduce = useReducedMotion();
  const transition = reduce ? { duration: 0 } : springs.snappy;
  const [endPicker, setEndPicker] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [subtasksOpen, setSubtasksOpen] = useState(false);
  const [subDraft, setSubDraft] = useState("");
  const tasks = useTasksStore((s) => s.tasks);
  const add = useTasksStore((s) => s.add);
  const edit = useTasksStore((s) => s.edit);
  const complete = useTasksStore((s) => s.complete);
  const remove = useTasksStore((s) => s.remove);
  const editing = p.editing;
  const parentId = editing ? (editing.virtual && editing.parent_id ? editing.parent_id : editing.id) : null;
  const subtasks = parentId ? childrenOf(tasks, parentId) : [];
  const progress = parentId ? subtaskProgress(tasks, parentId) : { done: 0, total: p.pendingSubtasks.length };
  const r: RuleInput = p.recurRule ?? { freq: "weekly", interval: 1 };
  const every = r.interval ?? 1;
  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    return Object.fromEntries(WEEKDAYS.map((d) => [d.key, formatter.format(d.date).replace(".", "")])) as Record<Weekday, string>;
  }, [locale]);

  useEffect(() => {
    if (progress.total > 0) setSubtasksOpen(true);
  }, [parentId, progress.total]);

  const setEnabled = (v: boolean) => {
    p.setRecurEnabled(v);
    if (v && !p.recurRule) p.setRecurRule({ freq: "weekly", interval: 1 });
  };
  const bump = (d: number) => p.setRecurRule({ ...r, interval: Math.min(99, Math.max(1, every + d)) });
  const toggleDay = (day: Weekday) => {
    const cur = r.byDay ?? [];
    const next = cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day];
    const { byDay: _drop, ...rest } = r;
    p.setRecurRule(next.length ? { ...rest, byDay: next } : rest);
  };
  const toggleReminder = (min: number) => {
    p.setReminderOffsets(
      p.reminderOffsets.includes(min)
        ? p.reminderOffsets.filter((m) => m !== min)
        : [...p.reminderOffsets, min].sort((a, b) => a - b),
    );
  };
  const addSub = () => {
    const t = subDraft.trim();
    if (!t) return;
    if (parentId) {
      void add({ title: t, subtask_parent_id: parentId, due_at: null, is_event: false, calendar_id: editing?.calendar_id ?? null, subtask_order: subtasks.length });
      setSubDraft("");
      return;
    }
    p.setPendingSubtasks([...p.pendingSubtasks, t]);
    setSubDraft("");
  };

  return (
    <div className="flex flex-col gap-2 px-1">
      <div className="flex flex-wrap gap-1.5">
        <button type="button" className={chip(p.recurEnabled)} onClick={() => setEnabled(!p.recurEnabled)}>
          {tr("taskForm.repeating")}
        </button>
        {p.showReminders && (
          <button type="button" className={chip(remindOpen || p.reminderOffsets.length > 0)} onClick={() => setRemindOpen(!remindOpen)}>
            {tr("taskForm.reminder")}
          </button>
        )}
        <button type="button" className={chip(subtasksOpen || progress.total > 0)} onClick={() => setSubtasksOpen(!subtasksOpen)}>
          {tr("taskForm.subtasks")}{progress.total > 0 ? ` ${progress.done}/${progress.total}` : ""}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {p.recurEnabled && (
          <motion.div
            key="repeat"
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 0, height: 0 } : { opacity: 0, height: 0 }}
            transition={transition}
            className="max-w-full overflow-hidden rounded-lg border bg-background p-2"
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">{tr("repeat.every")}</span>
                <button type="button" className="rounded border px-2 py-1 text-lg leading-none" onClick={() => bump(-1)}>-</button>
                <span className="min-w-6 text-center text-sm">{every}</span>
                <button type="button" className="rounded border px-2 py-1 text-lg leading-none" onClick={() => bump(1)}>+</button>
                <div className="no-scrollbar -mx-1 flex max-w-full flex-1 gap-1 overflow-x-auto px-1">
                  {FREQS.map((f) => (
                    <button key={f.id} type="button" className={toggleMini(r.freq === f.id)} onClick={() => p.setRecurRule({ ...r, freq: f.id })}>
                      {tr(f.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
              {r.freq === "weekly" && (
                <div className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      aria-label={weekdayLabels[d.key]}
                      className={cn("h-8 w-8 rounded-full border text-xs", (r.byDay ?? []).includes(d.key) ? "bg-primary text-primary-foreground" : "bg-background")}
                      onClick={() => toggleDay(d.key)}
                    >{weekdayLabels[d.key]}</button>
                  ))}
                </div>
              )}
              <button type="button" className="self-start text-sm text-muted-foreground underline" onClick={() => setEndPicker(true)}>
                {p.seriesUntilStr ? tr("repeat.endsOn", { date: p.seriesUntilStr }) : tr("repeat.noEndDate")}
              </button>
            </div>
          </motion.div>
        )}

        {p.showReminders && remindOpen && (
          <motion.div
            key="reminders"
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 0, height: 0 } : { opacity: 0, height: 0 }}
            transition={transition}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap gap-1.5">
              {REMINDERS.map((m) => (
                <button key={m.min} type="button" className={chip(p.reminderOffsets.includes(m.min))} onClick={() => toggleReminder(m.min)}>
                  {tr(m.labelKey, { count: m.count })}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {subtasksOpen && (
          <motion.div
            key="subtasks"
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 0, height: 0 } : { opacity: 0, height: 0 }}
            transition={transition}
            className="flex flex-col gap-1 overflow-hidden"
          >
            {parentId
              ? subtasks.map((t) => (
                  <div key={t.id} className={cn("flex items-center gap-2 rounded-md border bg-background px-2 py-1 text-sm", t.status === "done" && "opacity-65")}>
                    <input type="checkbox" checked={t.status === "done"} onChange={(e) => void complete(t.id, e.target.checked)} className="h-4 w-4 shrink-0 accent-primary" />
                    <input value={t.title} onChange={(e) => void edit(t.id, { title: e.target.value })} placeholder={tr("taskForm.subtask")} className={cn("min-w-0 flex-1 bg-transparent outline-none", t.status === "done" && "line-through")} />
                    <SubtaskPriority value={t.priority} onPick={(priority) => void edit(t.id, { priority })} />
                    <button type="button" aria-label={tr("taskForm.removeSubtask")} className="text-muted-foreground" onClick={() => void remove(t.id)}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              : p.pendingSubtasks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border bg-background px-2 py-1 text-sm">
                    <span className="truncate">{t}</span>
                    <button type="button" aria-label={tr("taskForm.removeSubtask")} className="ml-auto text-muted-foreground" onClick={() => p.setPendingSubtasks(p.pendingSubtasks.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
            <div className="flex items-center gap-2">
              <input
                value={subDraft}
                onChange={(e) => setSubDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                placeholder={tr("taskForm.addSubtask")}
                className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1.5 text-sm outline-none"
              />
              <button type="button" aria-label={tr("taskForm.addSubtask")} className="rounded-md border bg-background p-2" onClick={addSub}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SheetModal open={endPicker} onClose={() => setEndPicker(false)}>
        <div className="flex flex-col gap-2">
          <DatePicker
            value={p.seriesUntilStr ? new Date(`${p.seriesUntilStr}T00:00`) : null}
            onSelect={(d) => { p.setSeriesUntilStr(toLocalDate(d)); setEndPicker(false); }}
          />
          <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => { p.setSeriesUntilStr(""); setEndPicker(false); }}>
            {tr("repeat.noEndDate")}
          </button>
        </div>
      </SheetModal>
    </div>
  );
}

function toggleMini(on: boolean) {
  return cn("shrink-0 rounded-md border px-2 py-1 text-xs font-medium", on ? "bg-primary text-primary-foreground" : "bg-background");
}
